// Helpers around the `blasts` / `blast_recipients` / `blast_events` tables.
// These act as the "API" for the Outreach module — RLS in the database
// enforces that callers can only ever see their own merchant's data.

import { epicengageSupabasev1 } from "../integrations/supabase/client";

export type BlastChannel = "email" | "sms";
export type BlastStatus = "draft" | "scheduled" | "sending" | "sent";
export type RecipientStatus = "active" | "completed" | "exhausted" | "unsubscribed";
export type BlastEventType =
  | "delivered"
  | "opened"
  | "clicked"
  | "bounced"
  | "failed"
  | "unsubscribed";

export type BlastRow = {
  id: string;
  name: string;
  outlet_id: string;
  outlet_name: string;
  outlet_address: string | null;
  channel: BlastChannel;
  sequence_enabled: boolean;
  status: BlastStatus;
  review_url: string | null;
  email_subject: string | null;
  body_step1: string | null;
  body_step2: string | null;
  body_step3: string | null;
  cta_label: string;
  created_at: string;
  sent_at: string | null;
  scheduled_at: string | null;
  recipients: number;
  delivered: number;
  opened: number;
  clicked: number;
  openedPct: number | null; // null for SMS
  clickedPct: number;
  deliveredPct: number;
};

const pct = (num: number, den: number) => (den > 0 ? (num / den) * 100 : 0);

const aggregateRecipients = (
  recs: Array<{
    delivered_at: string | null;
    opened_at: string | null;
    clicked_at: string | null;
  }>,
) => {
  let delivered = 0;
  let opened = 0;
  let clicked = 0;
  for (const r of recs) {
    if (r.delivered_at) delivered += 1;
    if (r.opened_at) opened += 1;
    if (r.clicked_at) clicked += 1;
  }
  return { delivered, opened, clicked };
};

const SELECT = `
  id, outlet_id, channel, sequence_enabled, status, review_url,
  name, email_subject, body_step1, body_step2, body_step3, cta_label,
  created_at, sent_at, scheduled_at,
  outlets:outlet_id ( name, address ),
  blast_recipients ( delivered_at, opened_at, clicked_at )
`;

const toRow = (b: any): BlastRow => {
  const recs = (b.blast_recipients ?? []) as Array<{
    delivered_at: string | null;
    opened_at: string | null;
    clicked_at: string | null;
  }>;
  const { delivered, opened, clicked } = aggregateRecipients(recs);
  const recipients = recs.length;
  return {
    id: b.id,
    name: b.name ?? "Untitled campaign",
    outlet_id: b.outlet_id,
    outlet_name: b.outlets?.name ?? "—",
    outlet_address: b.outlets?.address ?? null,
    channel: b.channel,
    sequence_enabled: b.sequence_enabled,
    status: b.status,
    review_url: b.review_url ?? null,
    email_subject: b.email_subject,
    body_step1: b.body_step1,
    body_step2: b.body_step2,
    body_step3: b.body_step3,
    cta_label: b.cta_label,
    created_at: b.created_at,
    sent_at: b.sent_at,
    scheduled_at: b.scheduled_at ?? null,
    recipients,
    delivered,
    opened,
    clicked,
    openedPct: b.channel === "sms" ? null : pct(opened, recipients),
    clickedPct: pct(clicked, recipients),
    deliveredPct: pct(delivered, recipients),
  };
};

// GET /api/outreach/blasts — list blasts for account, with aggregated stats
export const fetchBlastsWithStats = async (merchantId: string): Promise<BlastRow[]> => {
  const { data, error } = await epicengageSupabasev1
    .from("blasts")
    .select(SELECT)
    .eq("merchant_id", merchantId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map(toRow);
};

// GET /api/outreach/blasts/:id — single blast with summary
export const fetchBlastById = async (id: string): Promise<BlastRow | null> => {
  const { data, error } = await epicengageSupabasev1
    .from("blasts")
    .select(SELECT)
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return toRow(data);
};

// GET /api/outreach/blasts/:id/recipients — paginated recipient list
export type RecipientRow = {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  current_step: number;
  sequence_status: RecipientStatus;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  step1_sent_at: string | null;
  step2_sent_at: string | null;
  step3_sent_at: string | null;
};

export const fetchRecipients = async (
  blastId: string,
  opts: { page?: number; pageSize?: number; search?: string } = {},
): Promise<{ rows: RecipientRow[]; total: number }> => {
  const page = Math.max(1, opts.page ?? 1);
  const pageSize = opts.pageSize ?? 20;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = epicengageSupabasev1
    .from("blast_recipients")
    .select(
      "id, name, email, phone, current_step, sequence_status, delivered_at, opened_at, clicked_at, step1_sent_at, step2_sent_at, step3_sent_at",
      { count: "exact" },
    )
    .eq("blast_id", blastId)
    .order("created_at", { ascending: true });

  const q = opts.search?.trim();
  if (q) {
    const safe = q.replace(/[,()]/g, " ");
    query = query.or(`name.ilike.%${safe}%,email.ilike.%${safe}%,phone.ilike.%${safe}%`);
  }

  const { data, error, count } = await query.range(from, to);
  if (error) throw error;
  return { rows: (data ?? []) as RecipientRow[], total: count ?? 0 };
};

// GET /api/outreach/blasts/:id/analytics — counts + rates
export type BlastAnalytics = {
  recipients: number;
  delivered: number;
  opened: number;
  clicked: number;
  deliveredPct: number;
  openedPct: number | null;
  clickedPct: number;
  perStep: Array<{ step: number; sent: number; opened: number; clicked: number }>;
};

export const fetchBlastAnalytics = async (blastId: string): Promise<BlastAnalytics> => {
  const { data: blast, error: bErr } = await epicengageSupabasev1
    .from("blasts")
    .select("channel")
    .eq("id", blastId)
    .single();
  if (bErr) throw bErr;

  const { data: recs, error: rErr } = await epicengageSupabasev1
    .from("blast_recipients")
    .select("delivered_at, opened_at, clicked_at, step1_sent_at, step2_sent_at, step3_sent_at")
    .eq("blast_id", blastId);
  if (rErr) throw rErr;

  const list = recs ?? [];
  const recipients = list.length;
  const { delivered, opened, clicked } = aggregateRecipients(list);

  const { data: events } = await epicengageSupabasev1
    .from("blast_events")
    .select("event_type, step")
    .eq("blast_id", blastId);

  const perStep = [1, 2, 3].map((step) => {
    const sentField = `step${step}_sent_at` as const;
    const sent = list.filter((r: any) => r[sentField]).length;
    const stepEvents = (events ?? []).filter((e: any) => e.step === step);
    return {
      step,
      sent,
      opened: stepEvents.filter((e: any) => e.event_type === "opened").length,
      clicked: stepEvents.filter((e: any) => e.event_type === "clicked").length,
    };
  });

  return {
    recipients,
    delivered,
    opened,
    clicked,
    deliveredPct: pct(delivered, recipients),
    openedPct: blast.channel === "sms" ? null : pct(opened, recipients),
    clickedPct: pct(clicked, recipients),
    perStep,
  };
};

// POST /api/outreach/blasts/:id/send — invoke send-blast edge function (returns 202)
export const sendBlast = async (id: string) => {
  const { error } = await epicengageSupabasev1.functions.invoke("send-blast", {
    body: { blast_id: id },
  });
  if (error) throw error;
};
